import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { useAnimatedAmount } from "@/shared/hooks/useAnimatedAmount";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

const amountDisplayVariants = cva("font-mono tabular-nums", {
  variants: {
    size: {
      xs: "text-xs font-medium",
      sm: "text-sm font-semibold",
      md: "text-base font-semibold",
      lg: "text-[28px] font-bold leading-none tracking-tight",
      xl: "text-[32px] font-bold leading-none tracking-tight",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

export interface ColocaAmountDisplayProps
  extends
    Omit<React.ComponentPropsWithoutRef<"span">, "children">,
    VariantProps<typeof amountDisplayVariants> {
  /**
   * The amount. Prefer the raw decimal **string** from the API (e.g. `"150000.00000000"`) — it is formatted
   * by Intl without converting to `Number`, so 8-decimal ledger amounts never lose precision.
   */
  value: string | number;
  /** ISO currency code rendered as a muted suffix (e.g. `"USD"`). Omit to render the number alone. */
  currency?: string;
  /**
   * Fraction digits shown (both minimum and maximum). Defaults to 2. Shorthand — for a range, pass
   * `minimumFractionDigits`/`maximumFractionDigits` instead.
   */
  fractionDigits?: number;
  /**
   * Minimum fraction digits. Overrides `fractionDigits` for the lower bound. Pair it with
   * `maximumFractionDigits` to render a range (e.g. `min 2 / max 8`) that preserves the API's real decimals
   * and never rounds the amount to fewer digits.
   */
  minimumFractionDigits?: number;
  /**
   * Maximum fraction digits. Overrides `fractionDigits` for the upper bound. Trailing zeros past the minimum
   * are trimmed, so `min 2 / max 8` renders `1,500.00` and `0.00012345` alike, with no precision loss.
   */
  maximumFractionDigits?: number;
  /** BCP 47 locale override. Defaults to the active app language. */
  locale?: string;
  /** Force an explicit sign: `+` for positive values (negatives always render `−`). */
  signed?: boolean;
  /** Extra classes for the currency suffix (it scales with the amount via `em` sizing by default). */
  currencyClassName?: string;
  /**
   * Count from the previously rendered amount to this one when it changes — for figures that move on their
   * own, like a balance updated by a socket push. Off by default: a form's total should not animate while the
   * user types. Never animates on first paint, and honours `prefers-reduced-motion`. The value stays the
   * authoritative decimal string; only the in-between frames are interpolated.
   */
  animate?: boolean;
  /** Shows asterisks instead of the figure (a balance the viewer chose to hide); the currency stays. */
  masked?: boolean;
}

/**
 * Intl-formatted monetary amount with an optional currency-code suffix.
 *
 * Use `ColocaAmountDisplay` anywhere a money value is shown — balances, statement rows, confirmation
 * summaries — so grouping, decimals and the muted currency suffix stay consistent across the dashboard. Pass
 * `signed` for movement lists where the direction matters (`+1,500.00` / `−1,500.00`).
 *
 * @summary Intl-formatted amount + currency suffix for balances and movements
 */
export const ColocaAmountDisplay = React.forwardRef<HTMLSpanElement, ColocaAmountDisplayProps>(
  function ColocaAmountDisplay(
    {
      value,
      currency,
      fractionDigits = 2,
      minimumFractionDigits,
      maximumFractionDigits,
      locale,
      signed = false,
      size,
      className,
      currencyClassName,
      animate = false,
      masked = false,
      ...rest
    },
    ref,
  ) {
    const { t, activeLanguage } = useTranslations("global");
    const resolvedLocale = locale ?? activeLanguage;

    // While counting, the frame is a plain number; at rest this is null and the caller's exact decimal
    // string is what renders, so an animation can never leave a rounded float on screen.
    const animatedFrame = useAnimatedAmount(value, animate);

    const settled = typeof value === "string" ? value.trim() : value;
    const isNegative = typeof settled === "string" ? settled.startsWith("-") : settled < 0;
    const settledMagnitude =
      typeof settled === "string" ? (isNegative ? settled.slice(1) : settled) : Math.abs(settled);

    const minFraction = minimumFractionDigits ?? fractionDigits;
    const maxFraction = Math.max(maximumFractionDigits ?? fractionDigits, minFraction);

    // Memoized: an animating amount re-renders every frame, and building a formatter per frame is the
    // expensive part of this component.
    const formatter = React.useMemo(
      () =>
        new Intl.NumberFormat(resolvedLocale, {
          minimumFractionDigits: minFraction,
          maximumFractionDigits: maxFraction,
          // `"always"`, not `true`. `true` honours the locale's own minimum, and `es` sets it to two groups
          // — four-digit numbers go unseparated per the RAE, so `1500.50` rendered `1500,50` here while the
          // grouped amount input beside it rendered `1.500,50` and `formatDecimalAmount2` (es-CO, whose
          // minimum is one) rendered `1.500,50` on the same screen. Three formatters, two answers, one
          // figure. In a dashboard the grouping is the app's voice rather than the reader's: the reader's
          // locale decides WHICH characters separate, and the app decides that it separates.
          useGrouping: "always",
        }),
      [resolvedLocale, minFraction, maxFraction],
    );

    // How many decimals the amount shows AT REST. A ledger string collapses its trailing zeros
    // ("250000.00000000" → 2 decimals under min 2 / max 8), but an interpolated frame is a float that
    // would fill the whole `maximumFractionDigits` budget — the count must stay pinned to the resting
    // width or the number visibly grows a tail of digits while it moves.
    const settledFractionDigits = React.useMemo(() => {
      const fraction = formatter
        .formatToParts(settledMagnitude as number)
        .find((part) => part.type === "fraction");
      return fraction ? fraction.value.length : 0;
    }, [formatter, settledMagnitude]);

    const frameFormatter = React.useMemo(
      () =>
        new Intl.NumberFormat(resolvedLocale, {
          minimumFractionDigits: settledFractionDigits,
          maximumFractionDigits: settledFractionDigits,
          // Same rule as the resting formatter above, for the same reason it is pinned to the resting
          // fraction width: an animating amount must not change SHAPE as it counts, only value.
          useGrouping: "always",
        }),
      [resolvedLocale, settledFractionDigits],
    );

    // Intl.NumberFormat v3 formats decimal strings exactly (no double-precision loss on 8-dp ledger
    // amounts); the TS ES2022 lib still types format() as number-only, hence the assertion.
    const formatted =
      animatedFrame !== null
        ? frameFormatter.format(Math.abs(animatedFrame))
        : formatter.format(settledMagnitude as number);
    const sign = isNegative ? "−" : signed ? "+" : "";

    return (
      <span ref={ref} className={cn(amountDisplayVariants({ size }), className)} {...rest}>
        {masked ? (
          <>
            <span aria-hidden="true" className="relative top-[0.22em]">
              ******
            </span>
            <span className="sr-only">{t("amount_hidden")}</span>
          </>
        ) : (
          <>
            {sign}
            {formatted}
          </>
        )}
        {currency ? (
          <span className={cn("ml-1.5 text-[0.6em] font-medium text-muted-foreground", currencyClassName)}>
            {currency}
          </span>
        ) : null}
      </span>
    );
  },
);
